import {useEffect, useRef} from "react";
import {useDisclosure} from "@chakra-ui/react";
import {
    useAuth,
    useCreateRequest,
    useNotification,
    useRequest,
    useChat,
    useVerification
} from "../../context";
import {OnboardingModal} from "../../components/RequestListing";
import {
    WEBSOCKET_RESPONSE_TYPE as socketType,
    WEBSOCKET_CLIENT_ACTIONS as actions
} from "../../helper/constants";
import {RequestAPIResponse} from "../../models";
import useWebSocket from '../../hooks/useWebsocket';

export function NotificationListener() {
    const modal = useDisclosure();
    const {state} = useNotification();
    const request = useCreateRequest();
    const requestList = useRequest();
    const handledRef = useRef<Set<string>>(new Set());
    const prevLengthRef = useRef<number>(0);
    const auth = useAuth();
    const chat = useChat();
    const verif = useVerification();
    const socket = useWebSocket({
        autoConnect: auth.status === "authenticated",
        onConnect: function () {
            socket.sendAction(actions.fetchIntents, {});
        },
        onMessage: function (msg) {
            console.info("received message: ", msg);
            switch (msg.type) {
                case socketType.creationRequestVerified: {
                    request.actions.resolve();
                    if (verif.has(msg.data.verificationID)) {
                        verif.actions.removeIntent(msg.data.verificationID);
                        requestList.actions.addRequest(
                            RequestAPIResponse.toRequest(msg.data)
                        );
                    }
                    break;
                }
                case socketType.messageSent: {
                    chat.actions.addMessages({
                        chatID: msg.data.chatID,
                        values: [msg.data]
                    });
                    setTimeout(readMessage(msg.data.id, msg.data.chatID), 3000);
                    break;
                }
                case socketType.readMessage: {
                    chat.actions.acknowledge(msg.data);
                    break;
                }
                case socketType.intentRevoked:
                    const {verificationID} = msg.data;
                    const val = verif.get(verificationID);
                    if (val && val.intent === "CREATION") {
                        request.dispatch(
                            {type: "set_request_state", payload: "failed"}
                        );
                    }
                    if (val && val.intent !== "CREATION") {
                        requestList.actions.updateActionState("failed");
                    }
                    verif.actions.removeIntent(msg.data.verificationID);
                    break;
                case socketType.assignmentRequestVerified: {
                    const {verificationID, requestID, resolver} = msg.data;
                    verif.actions.removeIntent(verificationID);
                    requestList.actions.handleAction({
                        action: {
                            data: {email: resolver.email ?? resolver},
                            type: "reassign"
                        },
                        requestId: requestID
                    });
                    requestList.actions.updateActionState("verified");
                    break;
                }
                case socketType.cancellationRequestVerified: {
                    const {verificationID, requestID} = msg.data;
                    verif.actions.removeIntent(verificationID);
                    requestList.actions.handleAction({
                        action: {type: "cancel"},
                        requestId: requestID
                    });
                    break;
                }
                case socketType.resolutionVerified: {
                    const {note, proceed, requestID, verificationID} = msg.data;
                    verif.actions.removeIntent(verificationID);
                    requestList.actions.handleAction({
                        action:{type: "resolve", data: {note, proceed}},
                        requestId: requestID
                    });
                    requestList.actions.updateActionState("verified");
                    break;
                }
                case socketType.intentsRetrieved: {
                    const {intents, itents} = msg.data;
                    verif.actions.setIntents(intents ?? itents ?? []);
                    break;
                };
                case socketType.identityVerified: {
                    modal.onOpen();
                    break;
                }
                default:
                    break;
            }
        }
    });

    function readMessage(id: string, chatId: string): () => void {
        return function () {
            if (chat.currentChat && chat.state.user) {
                chat.actions.acknowledge({
                    chatId,
                    readerId: chat.state.user.userID,
                    ids: [id]
                });
            }
        }
    }

    function resetIntentState() {
        if (verif.canCreate) {
            request.dispatch({type: "set_request_state", payload: "not-sent"});
            request.actions.setRedirect(true);
        }
        if (verif.canAct) {
            requestList.actions.updateActionState("not-sent");
        }
    }

    useEffect(function () {
        setTimeout(resetIntentState, 6000);
    }, [verif.state])
    useEffect(function () {
        if (auth.status === "authenticated") {
            socket.reconnect();
        }
        if (auth.status === "unauthenticated") {
            socket.disconnect();
        }
    }, [auth.status]);
    useEffect(function () {
        const notifications = state.notifications;
        if (notifications.length > prevLengthRef.current) {
            notifications.slice(prevLengthRef.current).forEach(function (notif) {
                if (handledRef.current.has(notif.id)) {
                    return;
                }
                handledRef.current.add(notif.id);
            });
        }
        prevLengthRef.current = notifications.length;
    }, [state.notifications]);
    return <OnboardingModal {...modal} role="identity-verified"/>
}

